import { cn } from '@/lib/utils';
import { Container } from '@/components/layout/Container';
import { Youtube } from 'lucide-react';
import type { ResourceHubFooterProps, ResourceHubFooterSocialLink } from '@/types/content';
import FacebookIcon from '@/icons/logo-facebook.svg?react';
import TwitterIcon from '@/icons/logo-x.svg?react';
import InstagramIcon from '@/icons/logo-instagram.svg?react';
import LinkedinIcon from '@/icons/logo-linkedin.svg?react';

/**
 * Returns the appropriate icon component for a social platform
 */
const getSocialIcon = (platform: ResourceHubFooterSocialLink['platform']) => {
  const icons = {
    facebook: FacebookIcon,
    twitter: TwitterIcon,
    instagram: InstagramIcon,
    linkedin: LinkedinIcon,
    youtube: Youtube
  };
  return icons[platform];
};

/**
 * ResourceHubFooter - Government-style footer for Ohio Resource Hub
 *
 * Features two sections:
 * - Top: Light gray section with 6 navigation links in a 3x2 grid
 * - Bottom: Dark blue bar with policy links and social media icons
 *
 * @example
 * ```tsx
 * import { ResourceHubFooter } from '@/components';
 * import footerContent from '@/content/resource-hub-footer.json';
 *
 * <ResourceHubFooter content={footerContent} />
 * ```
 */
export function ResourceHubFooter({ content, className }: ResourceHubFooterProps) {
  if (!content) {
    return null;
  }

  const { topSection, bottomBar } = content;

  return (
    <footer className={cn('mt-auto', className)}>
      {/* Top Section - Light Gray with Navigation Links */}
      <div
        className="pt-[40px] pb-[31px]"
        style={{ backgroundColor: topSection.backgroundColor || '#f7f7f7' }}
      >
        <Container>
          <nav aria-label="Footer navigation" className="flex justify-center items-start">
            <ul className="grid grid-cols-1 min-[800px]:grid-cols-3 gap-y-[10px] w-fit">
              {topSection.navLinks.map((link) => (
                <li key={link.id} className="w-[215.98px] h-[39px]">
                  <a
                    href={link.href}
                    target={link.external ? '_blank' : undefined}
                    rel={link.external ? 'noopener noreferrer' : undefined}
                    className={cn(
                      'block text-center -translate-y-[3px]',
                      'text-black text-[18px] font-semibold uppercase',
                      'hover:underline',
                      'transition-all duration-200',
                      'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                    )}
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        </Container>
      </div>

      {/* Bottom Section - Blue Bar with Policy Links and Social Icons */}
      <div
        className="py-[10px]"
        style={{ backgroundColor: bottomBar.backgroundColor || '#0e3f75' }}
      >
        <div className="mx-auto max-w-[1360px] px-[30px] min-[1000px]:grid min-[1000px]:grid-cols-[1fr_auto_1fr] min-[1000px]:items-center max-[999px]:flex max-[999px]:flex-col max-[999px]:items-center max-[999px]:gap-y-2">
          {/* Empty spacer for grid centering */}
          <div className="hidden min-[1000px]:block" />

          {/* Policy Links */}
          <nav aria-label="Policy links">
            <ul className="flex flex-col min-[800px]:flex-row flex-wrap justify-center items-center gap-x-[23px]">
              {bottomBar.policyLinks.map((link) => (
                <li key={link.id}>
                  <a
                    href={link.href}
                    target={link.external ? '_blank' : undefined}
                    rel={link.external ? 'noopener noreferrer' : undefined}
                    className={cn(
                      'text-white text-[15px] leading-[33px]',
                      'hover:text-[#0098D3] hover:underline',
                      'transition-colors duration-200',
                      'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white focus:ring-offset-[#0e3f75]'
                    )}
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          {/* Social Icons */}
          {bottomBar.socialLinks && bottomBar.socialLinks.length > 0 && (
            <nav aria-label="Social media links" className="min-[1000px]:justify-self-end">
              <ul className="flex items-center justify-center">
                {bottomBar.socialLinks.map((social) => {
                  const IconComponent = getSocialIcon(social.platform);
                  return (
                    <li key={social.id}>
                      <a
                        href={social.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={social.ariaLabel}
                        className={cn(
                          'flex items-center justify-center w-[45px] h-[34px]',
                          'transition-colors duration-200',
                          'focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#0e3f75]',
                          '[&_svg]:fill-white hover:[&_svg]:fill-[#0098D3]'
                        )}
                      >
                        <IconComponent className={cn('w-[30px] h-[30px]', social.platform === 'instagram' && 'translate-x-[1px]')} aria-hidden="true" />
                      </a>
                    </li>
                  );
                })}
              </ul>
            </nav>
          )}
        </div>
      </div>
    </footer>
  );
}

ResourceHubFooter.displayName = 'ResourceHubFooter';
